import { Box, Button, Dialog, FormControl, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import EquipmentInfo from "../EquipmentInfo";
import { i18n } from "../../translate/i18n";
import { DatePicker } from "@mui/x-date-pickers";
import { useDispatch, useSelector } from "react-redux";
import * as actions from '../../store/actions';
import Autocomplete from '../../UI/Autocomplete/Autocomplete';
import { IPostScheduledServiceBody, IUpdateScheduledServiceBody } from "../../store/actions/scheduledServices";
import { RootState } from "../../store/types";
import { LoadingButton } from "@mui/lab";

type Props = {
  equipment: {
    _id: string,
    photo?: {
      url: string
    },
    equipmentModel: {
      _id: string,
      name: string,
    },
    serialNumber: string,
    place: { _id: string, path: string },
    companies: { name: string }[],
    site: string,
  }
  serviceName: string,
  backPage: string,
  scheduledDate: null | Date,
  accountableId: string,
  accountableName: string,
  scheduledServiceId: string,
  closeModal: () => void,
  equipmentServicesReplicate: {
    expirationDate: null | Date,
    equipmentServiceId: {
      _id: string,
      frequency: {
        _id: string,
        name: string,
      },
      serviceType: {
        _id: string,
        name: string,
      }
    }
  },
  fetchData: () => void,
};

type IAutoCompleteOption = {label: string, value: string};

const ScheduleServiceModal: React.FC<Props> = (props) => {

  const dispatch = useDispatch();

  const page = 1;
  const limit = 50;
  const orderBy = 'name';
  const order = 'asc';

  const { equipment, equipmentServicesReplicate } = props;
  
  const [selectedDate, setSelectedDate] = useState<null | Date>(props.scheduledDate);
  const [searchTextUser, setSearchTextUser] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<IAutoCompleteOption>({label: "", value: ""});
  const [usersFormated, setUsersFormated] = useState<IAutoCompleteOption[]>([]);

  const [dateError, setDateError] = useState<boolean>(false);
  const [userError, setUserError] = useState<boolean>(false);

  const users = useSelector((state: RootState) => state.scheduledServices.modalUsers);
  const loadingUsers = useSelector((state: RootState) => state.scheduledServices.loadingModalUsers);
  const loadingPostUpdate = useSelector((state: RootState) => state.scheduledServices.loadingModalPostUpdateScheduledService);

  const fetchUsers = useCallback(
    () => dispatch(actions.fetchUsersForModal(page, limit, searchTextUser, order, orderBy)),
    [searchTextUser],
  );

  const postScheduledService = async (body: IPostScheduledServiceBody) => dispatch(actions.postScheduledServiceModal({ services: [body]}));
  const updateScheduledService = async (body: IUpdateScheduledServiceBody) => dispatch(actions.updateScheduledServiceModal(body, props.scheduledServiceId));

  const handleChangeSearchTextUsers = (text:string) => {
    setSearchTextUser(text);
  };

  useEffect(() => {fetchUsers();}, [searchTextUser]);
  useEffect(() => {
    setUsersFormated(users.map((user) => ({ label: user.name, value: user._id})))
    const userIndex = users.findIndex((user) => user._id === props.accountableId || user.name === props.accountableName);
    if (userIndex > -1 ) setSelectedUser({ label: users[userIndex].name, value: users[userIndex]._id})
  }, [users])


  const handleInputChangeUsers = (selected: IAutoCompleteOption | undefined) => {
    setUserError(false);
    if (selected) {
      setSelectedUser(selected);
    } else {
      setSelectedUser({label: "", value: ""})
    }
  };

  const handleSubmit = async () => {
    let error = false;

    if (!selectedDate) {
      error = true;
      setDateError(true);
    }

    if (selectedUser.value === "") {
      error = true;
      setUserError(true);
    }

    if (!error) {
      

      if (props.scheduledServiceId === "" ) {
        const body: IPostScheduledServiceBody = {
          accountables: [selectedUser.value],
          equipment: equipment._id,
          equipmentModelId: equipment.equipmentModel._id,
          equipmentModelName: equipment.equipmentModel.name,
          equipmentServiceId: equipmentServicesReplicate.equipmentServiceId._id,
          expirationDate: equipmentServicesReplicate.expirationDate || null,
          frequency: {
            _id: equipmentServicesReplicate.equipmentServiceId.frequency._id,
            name: equipmentServicesReplicate.equipmentServiceId.frequency.name,
          },
          serviceType: {
            _id: equipmentServicesReplicate.equipmentServiceId.serviceType._id,
            name: equipmentServicesReplicate.equipmentServiceId.serviceType.name,
          },
          place: equipment.place._id,
          site: equipment.site,
          scheduledDate: selectedDate!,
        }
        await postScheduledService(body);
      } else {
        const body = {
          accountables: [selectedUser.value],
          scheduledDate: selectedDate!,
        }
        await updateScheduledService(body);
      }
      props.fetchData();
      props.closeModal();
    }
  }

  return <Box sx={{minWidth: '600px', display: 'flex', flexDirection: 'column'}}>
    <Typography sx={{fontSize: 20, fontWeight: '500', letterSpacing: 0.15, wordWrap: 'break-word', padding: '16px 24px', borderBottom: '1px'}}>{i18n.t('scheduleServiceModal.title') as string}</Typography>
    <Box sx={{ borderTop: '1px rgba(0, 0, 0, 0.12) solid', borderBottom: '1px rgba(0, 0, 0, 0.12) solid', padding: '16px 24px' }}>
      <EquipmentInfo {...props} />
      <FormControl variant="standard" fullWidth margin="normal">
        <Autocomplete
          loading={loadingUsers}
          label={i18n.t('scheduleServiceModal.scheduleTo') as string}
          data={usersFormated}
          onSelectValue={handleInputChangeUsers}
          valueSelected={selectedUser}
          newSearchText={handleChangeSearchTextUsers}
          hasError={userError}
        />
      </FormControl>
      <DatePicker
        label={i18n.t('scheduleServiceModal.scheduledDate') as string}
        value={selectedDate ? new Date(selectedDate) : null}
        onChange={date => {setSelectedDate(date); setDateError(false)}}
        format="dd/MM/yyyy"
        slotProps={{ textField: { variant: 'standard', error: dateError }, }}
        sx={{ marginTop: '5px', width: '100%'}}
        minDate={new Date()}
      />
    </Box>
    <Box sx={{padding: '16px 24px', alignSelf: 'flex-end'}}>
      <Button onClick={() => props.closeModal()} sx={{color: 'red', marginRight: '10px', fontWeight: 500}}>{i18n.t('scheduleServiceModal.cancel') as string}</Button>
      <LoadingButton loading={loadingPostUpdate} onClick={handleSubmit} variant="contained" color='primary'>{i18n.t('scheduleServiceModal.schedule') as string}</LoadingButton>
    </Box>
  </Box>
};

export default ScheduleServiceModal;
